import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, Search, ShieldAlert, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  moduleIdsForSaasCenter,
  moduleLabel,
} from "@/lib/commercialLicenseRequests";
import { DEFAULT_MODULE_CATALOG } from "@/modules/catalogModulos";
import {
  applyCommercialReset,
  applyTestBuyerPurge,
  diagnoseCommercialTools,
  previewCommercialReset,
  previewTestBuyerPurge,
  reactivateCommercialWebLicense,
  renewCommercialWebLicense,
  suspendCommercialWebLicense,
  updateCommercialWebLicenseModules,
  fetchPlatformModules,
} from "@/lib/platformApi";
import {
  CAPABILITY_LABELS,
  COMMERCIAL_RESET_CONFIRM_PHRASE,
  COMMERCIAL_RESET_WARNING,
  COMMERCIAL_TOOLS_RESET_OPTION_LABELS,
  FUTURE_USER_LIFECYCLE_NOTE,
  TEST_BUYER_PURGE_BLOCKED_MESSAGE,
  TEST_BUYER_PURGE_CONFIRM_PHRASE,
  commercialToolsSearchFromQuery,
  defaultResetOptions,
  parseCommercialToolsError,
} from "@/lib/platformCommercialToolsUi";

function CapabilityBadges({ caps = {} }) {
  const entries = Object.entries(CAPABILITY_LABELS);
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, label]) => (
        <Badge
          key={key}
          variant="outline"
          className={
            caps[key]
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-slate-200 bg-slate-50 text-slate-500"
          }
        >
          {label}: {caps[key] ? "activo" : "off"}
        </Badge>
      ))}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
      <h3 className="text-sm font-semibold text-[#0F2D5C]">{title}</h3>
      {children}
    </section>
  );
}

function JsonList({ items, empty = "—" }) {
  if (!items?.length) return <p className="text-xs text-slate-500">{empty}</p>;
  return (
    <pre className="text-[11px] bg-slate-50 border rounded p-2 overflow-x-auto max-h-40">
      {JSON.stringify(items, null, 2)}
    </pre>
  );
}

export default function PlatformCommercialTools() {
  const [searchParams] = useSearchParams();
  const initial = useMemo(() => commercialToolsSearchFromQuery(searchParams), [searchParams]);

  const [email, setEmail] = useState(initial.email);
  const [rfc, setRfc] = useState(initial.rfc);
  const [installationId, setInstallationId] = useState(initial.installation_id);
  const [ownerUserId, setOwnerUserId] = useState(initial.owner_user_id);

  const [diagnosis, setDiagnosis] = useState(null);
  const [resetPreview, setResetPreview] = useState(null);
  const [resetOptions, setResetOptions] = useState(defaultResetOptions());
  const [confirmIdentifier, setConfirmIdentifier] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [resetReason, setResetReason] = useState("");

  const [diagnosing, setDiagnosing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [licenseBusy, setLicenseBusy] = useState(false);

  const [purgeEmail, setPurgeEmail] = useState(initial.email);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeConfirmPhrase, setPurgeConfirmPhrase] = useState("");
  const [purgeReason, setPurgeReason] = useState("");
  const [purgePreviewing, setPurgePreviewing] = useState(false);
  const [purging, setPurging] = useState(false);

  const [renewFrom, setRenewFrom] = useState("");
  const [renewTo, setRenewTo] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [moduleDraft, setModuleDraft] = useState({});
  const [moduleCatalog, setModuleCatalog] = useState(DEFAULT_MODULE_CATALOG);
  const saasModuleIds = useMemo(() => moduleIdsForSaasCenter(moduleCatalog), [moduleCatalog]);

  const queryPayload = useCallback(
    () => ({
      email: email.trim() || undefined,
      rfc: rfc.trim().toUpperCase() || undefined,
      installation_id: installationId.trim().toUpperCase() || undefined,
      owner_user_id: ownerUserId.trim() || undefined,
    }),
    [email, rfc, installationId, ownerUserId],
  );

  const primaryLicense = diagnosis?.organization_licenses?.[0] || null;

  useEffect(() => {
    fetchPlatformModules()
      .then((data) => {
        const items = data?.items;
        if (Array.isArray(items) && items.length) setModuleCatalog(items);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (primaryLicense?.modules) {
      setModuleDraft({ ...primaryLicense.modules });
    }
    if (primaryLicense?.valid_from) setRenewFrom(String(primaryLicense.valid_from).slice(0, 10));
    if (primaryLicense?.valid_to) setRenewTo(String(primaryLicense.valid_to).slice(0, 10));
  }, [primaryLicense?.id, primaryLicense?.modules, primaryLicense?.valid_from, primaryLicense?.valid_to]);

  const runDiagnose = async () => {
    const payload = queryPayload();
    if (!payload.email && !payload.rfc && !payload.installation_id && !payload.owner_user_id) {
      toast.error("Indique email, RFC, installation_id u owner_user_id.");
      return;
    }
    setDiagnosing(true);
    setResetPreview(null);
    try {
      const data = await diagnoseCommercialTools(payload);
      setDiagnosis(data);
      toast.success("Diagnóstico comercial listo (solo lectura).");
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
      setDiagnosis(null);
    } finally {
      setDiagnosing(false);
    }
  };

  const runResetPreview = async () => {
    if (!diagnosis) {
      toast.error("Ejecute el diagnóstico antes del preview de reinicio.");
      return;
    }
    setPreviewing(true);
    try {
      const data = await previewCommercialReset({
        ...queryPayload(),
        options: resetOptions,
      });
      setResetPreview(data);
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runResetApply = async () => {
    if (!diagnosis || !resetPreview) {
      toast.error("Genere el preview antes de aplicar el reinicio.");
      return;
    }
    if (resetPreview.blocked) {
      toast.error("Reinicio bloqueado: hay datos operativos/SAT asociados.");
      return;
    }
    setResetting(true);
    try {
      await applyCommercialReset({
        ...queryPayload(),
        options: resetOptions,
        confirm_identifier: confirmIdentifier.trim(),
        confirm_phrase: confirmPhrase.trim(),
        reason: resetReason.trim() || undefined,
      });
      toast.success("Reinicio comercial aplicado.");
      setResetPreview(null);
      setConfirmIdentifier("");
      setConfirmPhrase("");
      await runDiagnose();
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
    } finally {
      setResetting(false);
    }
  };

  const withLicenseAction = async (fn) => {
    if (!primaryLicense?.id) {
      toast.error("No hay licencia web en el diagnóstico.");
      return;
    }
    setLicenseBusy(true);
    try {
      const out = await fn(primaryLicense.id);
      toast.success("Licencia actualizada.");
      if (out?.capabilities_after) {
        setDiagnosis((prev) =>
          prev ? { ...prev, capabilities_summary: out.capabilities_after } : prev,
        );
      }
      await runDiagnose();
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
    } finally {
      setLicenseBusy(false);
    }
  };

  const runPurgePreview = async () => {
    const em = purgeEmail.trim();
    if (!em) {
      toast.error("Indique el correo a buscar.");
      return;
    }
    setPurgePreviewing(true);
    setPurgePreview(null);
    try {
      const data = await previewTestBuyerPurge(em);
      setPurgePreview(data);
      if (!data.found) {
        toast.message("Correo no encontrado.");
      } else if (data.can_purge) {
        toast.success("Vista previa lista — puede confirmar el borrado de prueba.");
      } else {
        toast.error(TEST_BUYER_PURGE_BLOCKED_MESSAGE);
      }
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
    } finally {
      setPurgePreviewing(false);
    }
  };

  const runPurgeApply = async () => {
    if (!purgePreview?.can_purge) {
      toast.error(TEST_BUYER_PURGE_BLOCKED_MESSAGE);
      return;
    }
    setPurging(true);
    try {
      await applyTestBuyerPurge({
        email: purgeEmail.trim(),
        confirm_phrase: purgeConfirmPhrase.trim(),
        reason: purgeReason.trim() || "limpieza de prueba sin licencia autorizada",
      });
      toast.success("Comprador de prueba eliminado. Revise auditoría en platform_audit_log.");
      setPurgeConfirmPhrase("");
      setPurgeReason("");
      await runPurgePreview();
    } catch (err) {
      toast.error(parseCommercialToolsError(err));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold text-[#0F2D5C] flex items-center gap-2">
          <Wrench className="w-5 h-5" aria-hidden />
          Herramientas comerciales
        </h2>
        <p className="text-sm text-slate-600 max-w-3xl">
          Diagnóstico, reinicio controlado del trámite de licencia y pruebas de módulos para compradores
          en la nube. Solo visible para el diseñador de la plataforma o con acceso al Centro en la nube.
        </p>
      </header>

      <SectionCard title="Buscar comprador">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="ct-email">Correo</Label>
            <Input id="ct-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ct-rfc">RFC</Label>
            <Input id="ct-rfc" value={rfc} onChange={(e) => setRfc(e.target.value.toUpperCase())} />
          </div>
          <div>
            <Label htmlFor="ct-iid">Identificador de instalación</Label>
            <Input
              id="ct-iid"
              value={installationId}
              onChange={(e) => setInstallationId(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <Label htmlFor="ct-owner">owner_user_id</Label>
            <Input id="ct-owner" value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247]"
            disabled={diagnosing}
            onClick={runDiagnose}
          >
            {diagnosing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
            Diagnóstico comercial
          </Button>
          <Button type="button" variant="outline" disabled={!diagnosis || diagnosing} onClick={runDiagnose}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Actualizar
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Limpiar comprador de prueba (sin licencia autorizada)">
        <p className="text-xs text-slate-600 leading-snug">
          Borra cuentas de registro/trial sin licencia emitida ni datos operativos (XML, SAT, FIEL/CSD).
          Solo diseñador / Centro SaaS. Requiere confirmación explícita y deja registro en auditoría.
        </p>
        <p className="text-[11px] text-slate-500 leading-snug">{FUTURE_USER_LIFECYCLE_NOTE}</p>
        <div className="max-w-md space-y-2 pt-1">
          <Label htmlFor="purge-email">Correo a buscar</Label>
          <Input
            id="purge-email"
            type="email"
            value={purgeEmail}
            onChange={(e) => setPurgeEmail(e.target.value)}
            data-testid="test-buyer-purge-email"
          />
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={purgePreviewing}
            onClick={runPurgePreview}
            data-testid="test-buyer-purge-preview-btn"
          >
            {purgePreviewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
            Vista previa
          </Button>
        </div>
        {purgePreview ? (
          <div className="mt-3 space-y-3 border-t pt-3 text-xs" data-testid="test-buyer-purge-preview">
            <dl className="grid sm:grid-cols-2 gap-2 m-0">
              <div>
                <dt className="text-slate-500">Usuario encontrado</dt>
                <dd className="font-medium">{purgePreview.user_found ? "Sí" : "No"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Correo</dt>
                <dd>{purgePreview.email || "—"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Razón social / nombre</dt>
                <dd>{purgePreview.profile?.razon_social || "—"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">RFC</dt>
                <dd className="font-mono">{purgePreview.profile?.rfc || "—"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Estado licencia</dt>
                <dd>{purgePreview.license_status || "—"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Periodo de prueba activo</dt>
                <dd>{purgePreview.trial_active ? "Sí" : "No"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Licencia autorizada</dt>
                <dd>{purgePreview.has_authorized_license ? "Sí" : "No"}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Clientes</dt>
                <dd>{purgePreview.operational_summary?.clients_count ?? 0}</dd>
              </div>
              {purgePreview.operational_summary?.client_credentials_placeholder > 0 ? (
                <div>
                  <dt className="text-slate-500">Placeholder credenciales</dt>
                  <dd>{purgePreview.operational_summary.client_credentials_placeholder}</dd>
                </div>
              ) : null}
            </dl>
            {purgePreview.purge_safe_notes?.length ? (
              <ul className="text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 space-y-1 list-disc pl-4 m-0">
                {purgePreview.purge_safe_notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
            <div>
              <p className="text-slate-500 mb-1">Solicitudes comerciales</p>
              <JsonList items={purgePreview.commercial_requests} empty="Ninguna" />
            </div>
            {purgePreview.blockers?.length > 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{purgePreview.blocked_message || TEST_BUYER_PURGE_BLOCKED_MESSAGE}</p>
                  <JsonList items={purgePreview.blockers} empty="" />
                </div>
              </div>
            ) : null}
            {purgePreview.can_purge ? (
              <>
                <p className="text-slate-600">
                  Entidades a eliminar: <strong>{purgePreview.planned_count}</strong>
                </p>
                <JsonList items={purgePreview.planned_deletions} empty="—" />
                <div>
                  <Label htmlFor="purge-confirm-phrase">
                    Confirmación: escriba {TEST_BUYER_PURGE_CONFIRM_PHRASE}
                  </Label>
                  <Input
                    id="purge-confirm-phrase"
                    value={purgeConfirmPhrase}
                    onChange={(e) => setPurgeConfirmPhrase(e.target.value)}
                    data-testid="test-buyer-purge-confirm-phrase"
                  />
                </div>
                <div>
                  <Label htmlFor="purge-reason">Motivo (auditoría)</Label>
                  <SpellcheckTextarea
                    id="purge-reason"
                    rows={2}
                    value={purgeReason}
                    onChange={(e) => setPurgeReason(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={purging || !purgePreview.apply_enabled}
                  onClick={runPurgeApply}
                  data-testid="test-buyer-purge-apply-btn"
                >
                  {purging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Borrar comprador de prueba
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
      </SectionCard>

      {diagnosis && (
        <>
          <SectionCard title="Resumen del diagnóstico (solo lectura)">
            {diagnosis.operational_blockers?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex gap-2">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Bloqueadores operativos detectados — el reinicio comercial estará bloqueado.</p>
                  <JsonList items={diagnosis.operational_blockers} empty="" />
                </div>
              </div>
            )}
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Capabilities actuales</p>
                <CapabilityBadges caps={diagnosis.capabilities_summary} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500">Usuario comprador</p>
                  <JsonList items={diagnosis.buyer_user ? [diagnosis.buyer_user] : []} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">system_config licencia</p>
                  <JsonList items={diagnosis.system_config} />
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500">Solicitudes comerciales</p>
                <JsonList items={diagnosis.license_requests} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500">Organizaciones</p>
                  <JsonList items={diagnosis.organizations} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Catálogo comprador</p>
                  <JsonList items={diagnosis.distributor_buyers} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500">Licencias emitidas</p>
                  <JsonList items={diagnosis.organization_licenses} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Cartera comercial</p>
                  <JsonList items={diagnosis.license_registry} />
                </div>
              </div>
              {!diagnosis.commercial_tools_apply_enabled && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  La aplicación del reinicio está deshabilitada en este entorno. Defina VF_COMMERCIAL_RESET_TOOLS=true en Railway para
                  aplicar el reinicio comercial.
                </p>
              )}
            </div>
          </SectionCard>

          {primaryLicense && (
            <SectionCard title="Acciones sobre licencia web">
              <p className="text-xs text-slate-600">
                Licencia: <span className="font-mono">{primaryLicense.id}</span> — estado{" "}
                <strong>{primaryLicense.status}</strong>
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={licenseBusy || primaryLicense.status === "suspended"}
                  onClick={() =>
                    withLicenseAction((licenseId) =>
                      suspendCommercialWebLicense({ license_id: licenseId, reason: actionReason.trim() || undefined }),
                    )
                  }
                >
                  Suspender licencia web
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={licenseBusy || primaryLicense.status === "active"}
                  onClick={() =>
                    withLicenseAction((licenseId) =>
                      reactivateCommercialWebLicense({
                        license_id: licenseId,
                        reason: actionReason.trim() || undefined,
                      }),
                    )
                  }
                >
                  Reactivar licencia
                </Button>
              </div>
              <div>
                <Label htmlFor="ct-action-reason">Motivo / comentario (auditoría)</Label>
                <Input
                  id="ct-action-reason"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Opcional"
                />
              </div>
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-medium text-slate-700">Actualizar módulos</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {saasModuleIds.map((mid) => (
                    <label key={mid} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={Boolean(moduleDraft[mid])}
                        onCheckedChange={(checked) =>
                          setModuleDraft((prev) => ({ ...prev, [mid]: Boolean(checked) }))
                        }
                      />
                      {moduleLabel(mid, moduleCatalog)}
                    </label>
                  ))}
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="bg-[#0F2D5C] hover:bg-[#0a2247]"
                  disabled={licenseBusy}
                  onClick={() =>
                    withLicenseAction((licenseId) =>
                      updateCommercialWebLicenseModules({
                        license_id: licenseId,
                        modules: moduleDraft,
                        reason: actionReason.trim() || undefined,
                      }),
                    )
                  }
                >
                  Guardar módulos
                </Button>
              </div>
              <div className="border-t pt-3 grid sm:grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="renew-from">Vigencia desde</Label>
                  <Input id="renew-from" type="date" value={renewFrom} onChange={(e) => setRenewFrom(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="renew-to">Vigencia hasta</Label>
                  <Input id="renew-to" type="date" value={renewTo} onChange={(e) => setRenewTo(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={licenseBusy || !renewFrom || !renewTo}
                    onClick={() =>
                      withLicenseAction((licenseId) =>
                        renewCommercialWebLicense({
                          license_id: licenseId,
                          valid_from: renewFrom,
                          valid_to: renewTo,
                          reason: actionReason.trim() || undefined,
                        }),
                      )
                    }
                  >
                    Renovar vigencia
                  </Button>
                </div>
              </div>
            </SectionCard>
          )}

          <SectionCard title="Reiniciar trámite de licencia / ciclo comercial">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <p>{COMMERCIAL_RESET_WARNING}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 pt-2">
              {Object.entries(COMMERCIAL_TOOLS_RESET_OPTION_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={Boolean(resetOptions[key])}
                    disabled={key === "delete_user"}
                    onCheckedChange={(checked) =>
                      setResetOptions((prev) => ({ ...prev, [key]: Boolean(checked) }))
                    }
                  />
                  <span className={key === "delete_user" ? "text-slate-500" : ""}>{label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="outline" disabled={previewing} onClick={runResetPreview}>
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Vista previa del reinicio
              </Button>
            </div>
            {resetPreview && (
              <div className="mt-3 space-y-3 border-t pt-3">
                <p className="text-xs text-slate-600">
                  Acciones planeadas: <strong>{resetPreview.planned_count}</strong>
                  {resetPreview.blocked ? " — bloqueado por datos operativos" : ""}
                </p>
                <JsonList items={resetPreview.planned_actions} empty="Sin acciones" />
                {!resetPreview.blocked && (
                  <>
                    <div>
                      <Label htmlFor="confirm-id">
                        Confirmación: escriba el RFC, el correo o el identificador de instalación exacto del comprador
                      </Label>
                      <Input
                        id="confirm-id"
                        value={confirmIdentifier}
                        onChange={(e) => setConfirmIdentifier(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-phrase">
                        Frase de confirmación: {COMMERCIAL_RESET_CONFIRM_PHRASE}
                      </Label>
                      <Input
                        id="confirm-phrase"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="reset-reason">Motivo (auditoría)</Label>
                      <SpellcheckTextarea
                        id="reset-reason"
                        rows={2}
                        value={resetReason}
                        onChange={(e) => setResetReason(e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={resetting || !diagnosis.commercial_tools_apply_enabled}
                      onClick={runResetApply}
                    >
                      {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Reiniciar trámite de licencia
                    </Button>
                  </>
                )}
              </div>
            )}
          </SectionCard>
        </>
      )}

      {!diagnosis && (
        <p className="text-sm text-slate-500">
          Ejecute el diagnóstico para ver acciones avanzadas. Desde{" "}
          <Link to="/platform/license-requests" className="text-[#0F2D5C] hover:underline">
            Solicitudes de licencia
          </Link>{" "}
          puede abrir esta pantalla con datos precargados.
        </p>
      )}
    </div>
  );
}
